import { memo } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";

import type { QuestionnaireResponseViewModel } from "../types";

export type QuestionnaireResponseViewProps = QuestionnaireResponseViewModel;

const QuestionnaireResponseView = memo((props: QuestionnaireResponseViewProps) => {
  const theme = useTheme();
  const {
    questionnaireTitle,
    questionnaireUsageLabel,
    questionnaireDescription,
    respondentSummary,
    respondedAtLabel,
    statusLabel,
    rows,
  } = props;

  const answerWellSx = {
    mt: 1.5,
    px: { xs: 1.75, sm: 2 },
    py: { xs: 1.5, sm: 1.75 },
    borderRadius: 1.5,
    bgcolor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.06)
        : alpha(theme.palette.primary.main, 0.04),
    border: "1px solid",
    borderColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.divider, 0.6)
        : alpha(theme.palette.primary.main, 0.12),
  } as const;

  return (
    <Stack spacing={{ xs: 2.5, sm: 3 }} sx={{ width: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
          borderRadius: 2,
          bgcolor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.paper, 1)
              : alpha(theme.palette.primary.main, 0.03),
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 0.6, display: "block", mb: 0.5 }}
            >
              Resumo
            </Typography>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {questionnaireTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {respondedAtLabel}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
              gap: { xs: 2, sm: 2.5 },
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Uso
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {questionnaireUsageLabel}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Quem respondeu
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {respondentSummary}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Status
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {statusLabel}
              </Typography>
            </Box>
          </Box>

          {questionnaireDescription ? (
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "min(100%, 72ch)" }}>
              {questionnaireDescription}
            </Typography>
          ) : null}
        </Stack>
      </Paper>

      <Box sx={{ width: "100%" }}>
        <Typography
          variant="h6"
          component="h2"
          sx={{ fontWeight: 700, mb: { xs: 2, sm: 2.5 } }}
        >
          Respostas
        </Typography>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhuma pergunta neste questionário.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, minmax(0, 1fr))",
              },
              gap: { xs: 2, sm: 2.5, md: 3 },
              alignItems: "stretch",
            }}
          >
            {rows.map((row) => (
              <Paper
                key={row.order}
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 2.25, md: 2.5 },
                  borderRadius: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid",
                  borderColor: row.isAnswerEmpty ? "warning.light" : "divider",
                  borderLeftWidth: 4,
                  borderLeftColor: row.isAnswerEmpty
                    ? "warning.main"
                    : "primary.main",
                  transition: theme.transitions.create(["border-color", "box-shadow"], {
                    duration: theme.transitions.duration.shorter,
                  }),
                  "&:hover": {
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.25)}`
                        : theme.shadows[2],
                  },
                }}
              >
                <Stack spacing={0} sx={{ flex: 1 }}>
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    gap={1}
                    sx={{ mb: 0.5 }}
                  >
                    <Typography
                      variant="subtitle1"
                      component="h3"
                      sx={{
                        fontWeight: 700,
                        lineHeight: 1.35,
                        flex: 1,
                        pr: row.isRequired ? 0.5 : 0,
                      }}
                    >
                      {row.questionLabel}
                    </Typography>
                    {row.isRequired ? (
                      <Chip
                        label="Obrigatória"
                        size="small"
                        color="default"
                        variant="filled"
                        sx={{
                          flexShrink: 0,
                          height: 24,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                        }}
                      />
                    ) : null}
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, letterSpacing: 0.02, mb: 0.25 }}
                  >
                    Resposta
                  </Typography>
                  <Box sx={answerWellSx}>
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        color: row.isAnswerEmpty ? "text.secondary" : "text.primary",
                        fontStyle: row.isAnswerEmpty ? "italic" : undefined,
                        lineHeight: 1.55,
                      }}
                    >
                      {row.answerLabel}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Box>
        )}
      </Box>
    </Stack>
  );
});

QuestionnaireResponseView.displayName = "QuestionnaireResponseView";

export default QuestionnaireResponseView;
